(function (root, factory) {
  const transport = factory();
  if (typeof module === "object" && module.exports) module.exports = transport;
  else root.EpicGuestChat = transport;
})(typeof window === "undefined" ? globalThis : window, () => {
  const KEY = "epic.wctek.guest.pending.v1";
  const validId = value => typeof value === "string" && /^[A-Za-z0-9_-]{8,128}$/.test(value);

  function createTransport({ api = window.EpicApi, storage = window.sessionStorage, now = Date.now } = {}) {
    let session = null, usage = null, messages = [], pending = null;
    let disposed = false, unavailable = false, notice = "", checkAfter = 0;
    let continuityChanged = false, loadPromise = null;
    const active = () => { if (disposed) throw new Error("Guest chat closed."); };
    async function request(path, options = {}) {
      active();
      const data = await api.request(`/olivia/guest${path}`, options);
      active();
      return data;
    }
    function remember(id) {
      const value = JSON.stringify({ id, continuityRef: session.continuityRef });
      storage.setItem(KEY, value);
      if (storage.getItem(KEY) !== value) throw new Error("Safe retry storage is unavailable.");
    }
    function clearPending() {
      if (pending && storage.getItem(KEY) === JSON.stringify({ id: pending.clientMessageId, continuityRef: session.continuityRef })) storage.removeItem(KEY);
      pending = null;
      checkAfter = 0;
    }
    function view() {
      const held = Boolean(pending || usage?.inFlight);
      const available = session?.capabilities?.text === true && !unavailable && !continuityChanged;
      const exhausted = usage && usage.remaining === 0 && !held;
      let message = "Ready";
      if (continuityChanged) message = "Guest session changed. Previous response needs reconciliation.";
      else if (!session || !usage) message = unavailable ? "Connection unavailable — check again" : "Connecting";
      else if (unavailable) message = "Connection unavailable — check again";
      else if (!available) message = "Free text is currently unavailable";
      else if (held) message = pending?.message ? "Checking your saved response" : "Response unresolved — check again";
      else if (exhausted) message = "Daily allowance used";
      else if (notice) message = notice;
      const reset = usage?.resetAt && new Date(usage.resetAt);
      const allowance = usage
        ? `${usage.remaining} replies remaining today${usage.reserved ? " · 1 reply held" : ""}. Resets ${reset && Number.isFinite(reset.getTime()) ? reset.toLocaleString() : "at midnight UTC"} (midnight UTC).`
        : "20 completed text replies per UTC day.";
      return { status: held ? "reconciling" : available && !exhausted ? "online" : "temporarily_unavailable",
        message, allowance, disabled: !available || !usage || held || exhausted || now() < checkAfter,
        retry: true, retryDisabled: now() < checkAfter, retryLabel: held ? "Check response" : "Refresh" };
    }
    const result = () => ({ thread: { messages: [...messages] }, usage, view: view() });

    function readUsage(value) {
      if (!value || ![value.used, value.reserved, value.remaining].every(Number.isSafeInteger) ||
          value.used < 0 || value.used > 20 || ![0, 1].includes(value.reserved) ||
          value.remaining !== Math.max(0, 20 - value.used - value.reserved) ||
          typeof value.inFlight !== "boolean" || value.inFlight !== Boolean(value.reserved) ||
          typeof value.resetAt !== "string" || !Number.isFinite(Date.parse(value.resetAt))) throw new Error("Allowance unavailable.");
      return value;
    }
    async function performLoad() {
      try {
        const fresh = await request("/session", { method: "POST", body: "{}", headers: { "X-WCTEK-Guest": "bootstrap" } });
        if (typeof fresh.csrfToken !== "string" || !/^[A-Za-z0-9_-]{32,128}$/.test(fresh.continuityRef) ||
            typeof fresh.capabilities?.text !== "boolean") throw new Error("Guest session unavailable.");
        const stored = storage.getItem(KEY);
        const saved = stored ? JSON.parse(stored) : null;
        if (session && session.continuityRef !== fresh.continuityRef || saved &&
            (!validId(saved.id) || saved.continuityRef !== fresh.continuityRef)) continuityChanged = true;
        session = fresh;
        if (continuityChanged) { pending = null; messages = []; usage = null; return result(); }
        const history = await request("/history", { method: "GET" });
        usage = readUsage(history.usage);
        messages = Array.isArray(history.messages) ? history.messages : [];
        unavailable = false;
        if (!pending && saved) pending = { clientMessageId: saved.id, message: null };
        // Recover a server-owned active turn even if this tab has no local ID.
        if (!pending && usage?.inFlight) {
          const unanswered = messages.filter(row => row.role === "user" && validId(row.clientMessageId) &&
            !messages.some(reply => reply.role === "assistant" && reply.clientMessageId === row.clientMessageId));
          const last = unanswered[unanswered.length - 1];
          if (last) { remember(last.clientMessageId); pending = { clientMessageId: last.clientMessageId, message: last.text }; }
        }
        if (pending) {
          const turn = messages.find(row => row.role === "user" && row.clientMessageId === pending.clientMessageId);
          if (turn) pending.message = turn.text;
          if (messages.some(row => row.role === "assistant" && row.clientMessageId === pending.clientMessageId)) clearPending();
          else {
            try {
              const status = await request(`/chat/${encodeURIComponent(pending.clientMessageId)}/status`, { method: "GET" });
              usage = readUsage(status.usage);
              checkAfter = now() + Math.min(60, Math.max(0, Number(status.pollAfterSeconds) || 0)) * 1000;
              if (status.status === "failed") { clearPending(); notice = "Previous request ended without a reply."; }
            } catch (error) {
              // Absence is not proof that an uncertain submission never ran.
              if (error.status !== 404) throw error;
            }
          }
        }
        return result();
      } catch (error) {
        unavailable = true;
        throw error;
      }
    }
    function load() {
      if (!loadPromise) loadPromise = performLoad().finally(() => { loadPromise = null; });
      return loadPromise;
    }
    function createRequest(text, clientMessageId) {
      active();
      if (view().disabled) throw new Error("Check the current response or allowance first.");
      if (!text || text.length > 4000 || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/u.test(text)) {
        throw new Error("Use 1–4,000 characters of text.");
      }
      if (!validId(clientMessageId)) throw new Error("Safe request identifier unavailable.");
      // Only opaque request/continuity references, never text or CSRF secrets.
      remember(clientMessageId);
      pending = { clientMessageId, message: text };
      return { ...pending };
    }
    async function send(payload) {
      active();
      if (!pending || pending.clientMessageId !== payload.clientMessageId || pending.message !== payload.message || !session?.csrfToken) {
        throw new Error("The original request is required for recovery.");
      }
      try {
        await request("/chat", { method: "POST", timeoutMs: 130000,
          headers: { "X-WCTEK-CSRF": session.csrfToken },
          body: JSON.stringify({ clientMessageId: pending.clientMessageId, message: pending.message }) });
        // Reload canonical history before clearing the pending identity.
        return await load();
      } catch (error) {
        if (!disposed) {
          try { await load(); } catch { unavailable = true; }
          checkAfter = Math.max(checkAfter, now() + Math.min(60, Math.max(0, Number(error.data?.retryAfterSeconds) || 0)) * 1000);
        }
        throw error;
      }
    }
    async function recover() {
      active();
      if (now() < checkAfter) return result();
      const data = await load();
      // Explicit user recovery only. GET status never submits or settles work.
      if (pending?.message && session?.capabilities?.text === true && !unavailable) return send({ ...pending });
      return data;
    }
    return { load, createRequest, send, recover, view, dispose() { disposed = true; session = null; messages = []; } };
  }
  return { createTransport };
});
